import { useEffect, useState } from 'react';
import BottomSheet from './BottomSheet';

const AttendanceSheet = ({
  open,
  onClose,
  student,
  activeCourses = [],
  onSubmit,
  saving,
  error,
}) => {
  const [selectedKey, setSelectedKey] = useState('');
  const [notes, setNotes] = useState('');

  useEffect(() => {
    if (!open) return;
    setNotes('');
    if (activeCourses.length === 1) {
      setSelectedKey(
        `${activeCourses[0].courseId}::${activeCourses[0].batchId || ''}`,
      );
    } else {
      setSelectedKey('');
    }
  }, [open, activeCourses]);

  const selected = activeCourses.find(
    (course) => `${course.courseId}::${course.batchId || ''}` === selectedKey,
  );

  const handleSubmit = () => {
    if (!selected) return;
    onSubmit?.({ course: selected, notes });
  };

  return (
    <BottomSheet
      open={open}
      onClose={onClose}
      title="Mark attendance"
      description={student ? `${student.displayName} — ${student.academyStudentId}` : ''}
      footer={
        <div className="flex items-center justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            className="min-h-[52px] rounded-2xl border border-stone-300 bg-white px-5 text-sm font-semibold text-stone-700"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSubmit}
            disabled={!selected || saving}
            className="min-h-[52px] rounded-2xl bg-[#d61f74] px-5 text-sm font-semibold text-white disabled:opacity-50"
          >
            {saving ? 'Saving...' : 'Mark present'}
          </button>
        </div>
      }
    >
      {!activeCourses.length ? (
        <div className="rounded-2xl border border-dashed border-stone-300 bg-stone-50 px-4 py-6 text-center text-sm text-stone-600">
          This student has no active enrollments. Register them for a course first.
        </div>
      ) : (
        <div className="space-y-4">
          <div>
            <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-stone-500">
              Active course
            </p>
            <div className="grid gap-2">
              {activeCourses.map((course) => {
                const key = `${course.courseId}::${course.batchId || ''}`;
                const active = key === selectedKey;
                return (
                  <button
                    key={key}
                    type="button"
                    onClick={() => setSelectedKey(key)}
                    className={`min-h-[64px] rounded-2xl border px-4 py-3 text-left transition ${
                      active
                        ? 'border-[#f4b6d2] bg-[#fdf2f8]'
                        : 'border-stone-200 bg-stone-50 hover:border-stone-300'
                    }`}
                  >
                    <p className="text-base font-semibold text-stone-950">
                      {course.courseName}
                    </p>
                    <p className="mt-0.5 text-xs text-stone-600">
                      {course.batchName} · {course.branch}
                    </p>
                    {course.scheduleDays?.length || course.scheduleTime ? (
                      <p className="mt-0.5 text-xs text-stone-500">
                        {(course.scheduleDays || []).join(', ')}
                        {course.scheduleTime ? ` · ${course.scheduleTime}` : ''}
                      </p>
                    ) : null}
                  </button>
                );
              })}
            </div>
          </div>

          <label className="block">
            <span className="mb-1.5 block text-sm font-medium text-stone-700">
              Notes (optional)
            </span>
            <textarea
              value={notes}
              onChange={(event) => setNotes(event.target.value)}
              rows={2}
              placeholder="Late arrival, early leave..."
              className="block w-full rounded-2xl border border-stone-300 px-4 py-3 text-sm focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-200"
            />
          </label>

          {error ? (
            <p className="rounded-2xl bg-red-50 px-4 py-2 text-sm text-red-700">
              {error}
            </p>
          ) : null}
        </div>
      )}
    </BottomSheet>
  );
};

export default AttendanceSheet;
